import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { CartProvider } from './context/CartContext';
import AdminCollectionsPage  from './pages/admin/AdminCollectionsPage';
import AdminSeoPage from './pages/admin/AdminSeoPage';

// Layout
import Navbar      from './components/Navbar';
import Footer      from './components/Footer';
import CartDrawer  from './components/CartDrawer';
import Toast       from './components/Toast';

// Public pages
import SeoLandingPage from './pages/SeoLandingPage';
import HomePage          from './pages/HomePage';
import ShopPage          from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CheckoutPage      from './pages/CheckoutPage';
import { OrderSuccessPage } from './pages/OrderSuccessPage';
import TrackOrderPage    from './pages/TrackOrderPage';

// Policy pages
import PrivacyPolicyPage  from './pages/PrivacyPolicyPage';
import RefundPolicyPage   from './pages/RefundPolicyPage';
import TermsPage          from './pages/TermsPage';
import ContactPage        from './pages/ContactPage';
import ShippingPolicyPage from './pages/ShippingPolicyPage';
import AboutPage          from './pages/AboutPage';
import SizeGuidePage      from './pages/SizeGuidePage';
import CareGuidePage      from './pages/CareGuidePage';

// Admin pages
import AdminLoginPage        from './pages/admin/AdminLoginPage';
import AdminLayout           from './pages/admin/AdminLayout';
import AdminDashboard        from './pages/admin/AdminDashboard';
import AdminOrdersPage       from './pages/admin/AdminOrdersPage';
import AdminOrderDetailPage  from './pages/admin/AdminOrderDetailPage';
import AdminProductsPage     from './pages/admin/AdminProductsPage';
import AdminProductFormPage  from './pages/admin/AdminProductFormPage';
import AdminImagesPage       from './pages/admin/AdminImagesPage';
import AdminMockupsPage      from './pages/admin/AdminMockupsPage';
import NotFoundPage          from './pages/NotFoundPage';

import BlogListPage          from './pages/Bloglistpage';
import BlogDetailPage        from './pages/Blogdetailpage';
import CollectionDetailPage  from './pages/Collectiondetailpage';
import AdminBlogPage         from './pages/admin/Adminblogpage';
import AdminBlogFormPage     from './pages/admin/Adminblogformpage';
import AdminCollectionFormPage from './pages/admin/Admincollectionsformpage';
import AdminProductImportPage  from './pages/admin/Adminproductimportpage';
 

// Storefront wrapper — includes Navbar, Footer, Cart
function StoreLayout({ children }) {
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer />
      <CartDrawer />
      <Toast />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <BrowserRouter>
          <Routes>

            {/* ── Public / Storefront ── */}
            <Route path="/" element={
              <StoreLayout><HomePage/></StoreLayout>
            }/>
            <Route path="/shop" element={
              <StoreLayout><ShopPage/></StoreLayout>
            }/>
            <Route path="/product/:slug" element={
              <StoreLayout><ProductDetailPage/></StoreLayout>
            }/>
            <Route path="/checkout" element={
              <StoreLayout><CheckoutPage/></StoreLayout>
            }/>
            <Route path="/order-success" element={
              <StoreLayout><OrderSuccessPage/></StoreLayout>
            }/>
            <Route path="/track-order" element={
              <StoreLayout><TrackOrderPage/></StoreLayout>
            }/>
            <Route path="/privacy-policy" element={
              <StoreLayout><PrivacyPolicyPage/></StoreLayout>
            }/>
            <Route path="/refund-policy" element={
              <StoreLayout><RefundPolicyPage/></StoreLayout>
            }/>
            <Route path="/terms" element={
              <StoreLayout><TermsPage/></StoreLayout>
            }/>
            <Route path="/contact" element={
              <StoreLayout><ContactPage/></StoreLayout>
            }/>
            <Route path="/about" element={
              <StoreLayout><AboutPage/></StoreLayout>
            }/>
            <Route path="/size-guide" element={
              <StoreLayout><SizeGuidePage/></StoreLayout>
            }/>
            <Route path="/care-guide" element={
              <StoreLayout><CareGuidePage/></StoreLayout>
            }/>
            <Route path="/shipping-policy" element={
              <StoreLayout><ShippingPolicyPage/></StoreLayout>
            }/>

            <Route path="/custom" element={<StoreLayout><SeoLandingPage/></StoreLayout>}/>
            <Route path="/custom/:slug" element={<StoreLayout><SeoLandingPage/></StoreLayout>}/>
            <Route path="/blog" element={<StoreLayout><BlogListPage/></StoreLayout>}/>
            <Route path="/blog/:slug" element={<StoreLayout><BlogDetailPage/></StoreLayout>}/>
            <Route path="/collection/:slug" element={<StoreLayout><CollectionDetailPage/></StoreLayout>}/>

            {/* ── Admin ── */}
            <Route path="/admin/login" element={<AdminLoginPage/>}/>

            {/* Admin layout with nested routes */}
            <Route path="/admin" element={<AdminLayout/>}>
              <Route index element={<Navigate to="dashboard" replace/>}/>
              <Route path="dashboard"              element={<AdminDashboard/>}/>
              <Route path="orders"                 element={<AdminOrdersPage/>}/>
              <Route path="orders/:id"             element={<AdminOrderDetailPage/>}/>
              <Route path="products"               element={<AdminProductsPage/>}/>
              <Route path="products/new"           element={<AdminProductFormPage/>}/>
              <Route path="products/:id/edit"      element={<AdminProductFormPage/>}/>
              <Route path="collections"            element={<AdminCollectionsPage/>}/>
              <Route path="images"                 element={<AdminImagesPage/>}/>
              <Route path="mockups"                element={<AdminMockupsPage/>}/>

              <Route path="blog"               element={<AdminBlogPage/>}/>
              <Route path="blog/new"           element={<AdminBlogFormPage/>}/>
              <Route path="blog/:id/edit"      element={<AdminBlogFormPage/>}/>
              <Route path="collections"        element={<AdminCollectionsPage/>}/>
              <Route path="collections/new"    element={<AdminCollectionFormPage/>}/>
              <Route path="collections/:id/edit" element={<AdminCollectionFormPage/>}/>
              <Route path="products/import"     element={<AdminProductImportPage/>}/>
              <Route path="seo"                  element={<AdminSeoPage/>}/>
            </Route>

            {/* Fallback — real 404, not a redirect. Better for SEO crawl budget. */}
            <Route path="*" element={
              <StoreLayout><NotFoundPage /></StoreLayout>
            }/>

          </Routes>
        </BrowserRouter>
      </CartProvider>
    </HelmetProvider>
  );
}